import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Chip
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  FileDownload as DownloadIcon,
  PictureAsPdf as PdfIcon,
  Description as ExcelIcon,
  Assessment as ReportIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../services/api';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const STATUS_COLORS = {
  'assigned': '#4caf50',
  'ready-to-use': '#2196f3',
  'in-repair': '#f44336',
  'not-ready-to-use': '#ff9800',
  'no-use': '#9e9e9e',
  'retired': '#607d8b'
};

const MANUFACTURER_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00C49F', '#FFBB28', '#FF8042', '#0088FE', '#00C49F', '#FFBB28'];

const Reports = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    // Set default date range to last 6 months
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 6);
    setStartDate(start);
    setEndDate(end);
  }, []);

  const loadReportData = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {};
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();

      const response = await api.get('/reports/inventory', { params });

      if (response.data.success) {
        setReportData(response.data.data);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = () => {
    loadReportData();
  };

  const handleQuickRange = (months) => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - months);
    setStartDate(start);
    setEndDate(end);
  };

  const downloadExcel = async () => {
    try {
      setDownloading(true);
      const params = {};
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();

      const response = await api.get('/reports/excel', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `laptop-inventory-report-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setError('Failed to download Excel report');
    } finally {
      setDownloading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      setDownloading(true);
      const params = {};
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();

      const response = await api.get('/reports/pdf', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `laptop-inventory-report-${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setError('Failed to download PDF report');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton color="inherit" onClick={() => navigate('/')} sx={{ mr: 2 }}>
              <BackIcon />
            </IconButton>
            <ReportIcon sx={{ mr: 2 }} />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Inventory Reports
            </Typography>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Generate Report
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<ReportIcon />}
                  onClick={handleGenerateReport}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Generate Report'}
                </Button>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 2, alignSelf: 'center' }}>
                Quick Select:
              </Typography>
              <Button size="small" variant="outlined" onClick={() => handleQuickRange(1)}>
                Last Month
              </Button>
              <Button size="small" variant="outlined" onClick={() => handleQuickRange(3)}>
                Last 3 Months
              </Button>
              <Button size="small" variant="outlined" onClick={() => handleQuickRange(6)}>
                Last 6 Months
              </Button>
              <Button size="small" variant="outlined" onClick={() => handleQuickRange(12)}>
                Last Year
              </Button>
            </Box>
          </Paper>

          {reportData && (
            <>
              {/* Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom variant="body2">
                        Total Laptops
                      </Typography>
                      <Typography variant="h4">{reportData.summary.totalLaptops}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom variant="body2">
                        Total Changes
                      </Typography>
                      <Typography variant="h4">{reportData.summary.totalChanges}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom variant="body2">
                        Total Repairs
                      </Typography>
                      <Typography variant="h4">{reportData.summary.totalRepairs}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom variant="body2">
                        Total Repair Cost
                      </Typography>
                      <Typography variant="h4">
                        ${reportData.summary.totalRepairCost.toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Status Distribution Chart */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Status Distribution
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={reportData.summary.statusDistribution.map(stat => ({
                            name: stat._id,
                            value: stat.count,
                            percentage: ((stat.count / reportData.summary.totalLaptops) * 100).toFixed(1)
                          }))}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percentage }) => `${name}: ${percentage}%`}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {reportData.summary.statusDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry._id] || '#8884d8'} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [`${value} laptops`, name]} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart
                        data={reportData.summary.statusDistribution.map(stat => ({
                          name: stat._id,
                          count: stat.count
                        }))}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="name" type="category" width={90} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#8884d8">
                          {reportData.summary.statusDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry._id] || '#8884d8'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2, justifyContent: 'center' }}>
                  {reportData.summary.statusDistribution.map((stat) => (
                    <Chip
                      key={stat._id}
                      label={`${stat._id}: ${stat.count} (${((stat.count / reportData.summary.totalLaptops) * 100).toFixed(1)}%)`}
                      sx={{ backgroundColor: STATUS_COLORS[stat._id] || '#8884d8', color: 'white' }}
                      size="medium"
                    />
                  ))}
                </Box>
              </Paper>

              {/* Manufacturer Distribution Chart */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Manufacturer Distribution
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={reportData.summary.manufacturerDistribution.slice(0, 10).map(stat => ({
                            name: stat._id,
                            value: stat.count
                          }))}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={120}
                          fill="#82ca9d"
                          dataKey="value"
                        >
                          {reportData.summary.manufacturerDistribution.slice(0, 10).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={MANUFACTURER_COLORS[index % MANUFACTURER_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [`${value} laptops`, name]} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart
                        data={reportData.summary.manufacturerDistribution.slice(0, 10).map(stat => ({
                          name: stat._id,
                          count: stat.count
                        }))}
                        margin={{ top: 5, right: 30, left: 20, bottom: 70 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} height={80} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#82ca9d">
                          {reportData.summary.manufacturerDistribution.slice(0, 10).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={MANUFACTURER_COLORS[index % MANUFACTURER_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, justifyContent: 'center' }}>
                  {reportData.summary.manufacturerDistribution.map((stat, index) => (
                    <Chip
                      key={stat._id}
                      label={`${stat._id}: ${stat.count}`}
                      sx={{ backgroundColor: MANUFACTURER_COLORS[index % MANUFACTURER_COLORS.length], color: 'white' }}
                      size="small"
                    />
                  ))}
                </Box>
              </Paper>

              {/* Download Options */}
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Download Report
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      startIcon={<ExcelIcon />}
                      onClick={downloadExcel}
                      disabled={downloading}
                      color="success"
                    >
                      Download Excel
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Complete detailed report with all laptop data, summary, and repairs
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      startIcon={<PdfIcon />}
                      onClick={downloadPDF}
                      disabled={downloading}
                      color="error"
                    >
                      Download PDF
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Summary report with status distribution and complete laptop listing
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </>
          )}

          {!reportData && !loading && (
            <Paper sx={{ p: 5, textAlign: 'center' }}>
              <DownloadIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Select a date range and click "Generate Report" to view data
              </Typography>
            </Paper>
          )}
        </Container>
      </Box>
    </LocalizationProvider>
  );
};

export default Reports;
